import {infoIcon} from '@img/index.js'
import FlatRateBox from './FlatRateBox.jsx'

// Same money rule as CarDetailsCard: every line to the cent, the LAST line (the total)
// rounded UP to whole dollars so it matches the figure the price panel quotes.
// Not exported — a file that exports both a component and a plain function loses fast
// refresh; CarDetailsCard keeps its own copy, so keep the two in step by hand.
function formatBreakdownValue(val, isLast) {
    if (isLast) return val < 0 ? `-$${Math.ceil(Math.abs(val / 100))}` : `$${Math.ceil(val / 100)}`
    return val < 0 ? `-$${Math.abs(val / 100).toFixed(2)}` : `$${(val / 100).toFixed(2)}`
}

/**
 * Read-only pricing breakdown for a trip's quoted vehicle — the rows only, no card, no
 * header, no controls.
 *
 * This is what the price panel's ⓘ opens. The booking flow's equivalent popup renders
 * `PricingBreakdownList`, which reads the quote out of sessionStorage via
 * `filterSelectedCarDetails()` — useless on a trip page, where the numbers live on
 * `trip.selected_vehicle`. Hence a second, simpler renderer rather than a prop on that
 * one.
 *
 * Deliberately NOT extracted out of CarDetailsCard: that card's rows carry a live
 * gratuity dropdown and its own airport-fee modal, and pulling them apart would put the
 * /edit, /confirm and /view pages at risk to save a dozen lines. Here gratuity is a flat
 * pill and the airport-fee ⓘ is handed upwards, because a popup opened from a popup is
 * the caller's problem to sequence.
 *
 * @param {object}   props.trip              trip whose `selected_vehicle` holds the quote
 * @param {string|number} [props.gratuityPercent] shown as a pill on the gratuity row
 * @param {function} [props.onAirportFeeClick]    ⓘ next to the airport pickup fee
 */
export default function TripPriceBreakdown({trip, gratuityPercent, onAirportFeeClick}) {
    const vehicle = trip?.selected_vehicle
    const items = vehicle?.pricing_breakdown || vehicle?.line_by_line_breakdown || []

    if (!vehicle) return <p className="text-sm text-(--text-secondary) mb-0!">No price to show yet.</p>

    if (items.length === 0) {
        return (
            <>
                <div className="flex justify-between items-center text-sm">
                    <span>Total</span>
                    <span className="text-lg font-bold">${Math.ceil((vehicle.total_price || 0) / 100)}</span>
                </div>
                <FlatRateBox/>
            </>
        )
    }

    return (
        <>
            {items.filter((item) => Number(item.value) !== 0).map((item, index, arr) => {
                const isLast = index === arr.length - 1
                return (
                    <div key={item.key || index} className="flex justify-between items-center text-sm border-b border-[#CFCDCD] pb-4 mb-4">
                        <span className="flex flex-wrap items-center gap-x-1">
                            {item.label}
                            {item.key === 'gratuity' && gratuityPercent != null && (
                                <span className="text-sm border border-(--border-color) rounded-full px-3 py-1 ml-1">{gratuityPercent}%</span>
                            )}
                            {item.key === 'airport_pickup_fee' && onAirportFeeClick && (
                                <button type="button" onClick={onAirportFeeClick}
                                        className="cursor-pointer bg-transparent border-0 p-0">
                                    <img src={infoIcon} alt="Info" className="w-4 h-4"/>
                                </button>
                            )}
                        </span>
                        <span className="font-semibold text-lg">{formatBreakdownValue(item.value, isLast)}</span>
                    </div>
                )
            })}
            <FlatRateBox/>
        </>
    )
}
